import { useQuery } from '@tanstack/vue-query'
import queryClient, { queryKeys } from '~/queryClient'
import financialBff from '~/services-v2/financial-bff/financial-bff'

const CANCELED_FINANCING_STATUS = 'canceled'

function isCanceledFinancingStatus(status: string | undefined) {
  return status === CANCELED_FINANCING_STATUS
}

export default function useFinancing(id: Ref<string>, enableRefetch = true) {
  const REFETCH_INTERVAL = 15_000 // 15 seconds

  const queryKey = computed(() => queryKeys.FINANCING.DETAILS(id.value))

  const refetchInterval = ref(enableRefetch ? REFETCH_INTERVAL : 0)

  const enabled = computed(() => true)

  function resetRefetchInterval() {
    if (!enableRefetch)
      return

    refetchInterval.value = 0
    nextTick(() => {
      refetchInterval.value = REFETCH_INTERVAL
    })
  }

  const queryFn = async () => {
    const res = await financialBff('query')({
      financingV2: [{ id: id.value }, {
        id: true,
        legacy_id: true,
        project_id: true,
        customer_name: true,
        status: true,
        person_type: true,
        step: true,
        financed_value: true,
        flow_provider: true,
        formalized_at: true,
        created_at: true,
        updated_at: true,
        expires_at: true,
        installed_at: true,
        is_aproved: true,
        is_combo: true,
        is_combo_connect: true,
        is_expired: true,
        is_final: true,
        new_journey: true,
        reason: true,
        system_power: true,
        inverters_power_kw: true,
        document: true,
        all_payments_sent: true,
        payments: {
          id: true,
          value: true,
          financing_id: true,
          total_value: true,
          payment_percentage: true,
          created_at: true,
        },
        section_statuses: {
          simulation: {
            completed_at: true,
            reproved_at: true,
            status: true,
            submitted_at: true,
          },
          installation: {
            completed_at: true,
            reproved_at: true,
            status: true,
            submitted_at: true,
          },
          customer_data: {
            completed_at: true,
            reproved_at: true,
            status: true,
            submitted_at: true,
          },
          equipment: {
            completed_at: true,
            reproved_at: true,
            status: true,
            submitted_at: true,
          },
          biometry_and_contract: {
            completed_at: true,
            reproved_at: true,
            status: true,
            submitted_at: true,
            metadata: {
              biometry: {
                completed_at: true,
                first_submitted_at: true,
                last_submitted_at: true,
              },
              contract: {
                completed_at: true,
                first_submitted_at: true,
                last_submitted_at: true,
              },
            },
          },
          payment_model: {
            completed_at: true,
            reproved_at: true,
            status: true,
            submitted_at: true,
          },
        },
        openable_steps: {
          registration: true,
          documentation: true,
          hardware: true,
          contract: true,
          receiptModel: true,
        },
      }],
    }).catch((err) => {
      refetchInterval.value = 0 // Stop refetching on error
      throw err
    })

    const oldData = queryClient.getQueryData<typeof res.financingV2>(queryKey.value)

    if (oldData && res.financingV2) {
      const oldStatus = oldData.section_statuses
      const newStatus = res.financingV2.section_statuses
      if (oldStatus?.customer_data?.status !== newStatus?.customer_data?.status) {
        await queryClient.refetchQueries({ queryKey: queryKeys.REGISTRATION.DATA(id.value) })
      }
    }

    if (isCanceledFinancingStatus(res.financingV2?.status)) {
      refetchInterval.value = 0
    }
    else {
      resetRefetchInterval()
    }

    return res.financingV2!
  }

  return useQuery({
    queryFn,
    queryKey,
    refetchInterval,
    enabled,
  })
}

export type UseFinancingData = ReturnType<typeof useFinancing>['data']['value']
export type SectionStatuses = NonNullable<ReturnType<typeof useFinancing>['data']['value']>['section_statuses']
export type UseFinancingError = ReturnType<typeof useFinancing>['error']['value']
